import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { renderToString } from 'react-dom/server';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { chartMonthDateRangeOptions } from '@/modules/common/constants/chart-date-range-oprions.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import BalancePieChart from '@/modules/issuer-dashboard/components/BalancePieChart.tsx';
import {
  useIssuerGetPendingApplicationSummaryChartQuery,
  useIssuerGetPendingApplicationSummarySectionQuery,
} from '@/modules/issuer-dashboard/queries/charts.ts';
import { AnimatedNumbersSet, BalanceChartCanvas, InfoCard, SelectButtons, Spinner, Tooltip } from 'ui';
import { InfoCircleIcon } from 'ui/icons';
import { goodFormatNumber, goodFormatUSDCurrencyLowPrecision } from 'ui/utils';
import { ApplicationSummaryResponse } from 'api';

const AOTQueryKey = 'aot';

const adaptApplicationsOverTimeData = (
  data: ApplicationSummaryResponse[] | undefined,
  AOTQueryParam: string | null,
) => {
  if (!data) return [];

  return AOTQueryParam || !data || !data.length
    ? data
    : [
        {
          date: dayjs(data[0].date).subtract(1, 'day').format('YYYY-MM-DD'),
          awaiting_payment: 0,
          incomplete: 0,
          kyc_pending: 0,
          pending: 0,
        },
        ...data,
      ];
};

const applicationsOverTimeChartColors = ['#0369A1', '#0278B5', '#0389C7', '#01A0D8'];

const mapSummaryKeyToTitle: Record<string, string> = {
  pending: 'Pending',
  unpaid: 'Unpaid',
  kyc_pending: 'KYC Pending',
  incomplete: 'Incomplete',
};

const mapSummaryKeyToTooltipInfo: Record<string, string> = {
  pending: 'Total pending applications of investors which finished KYC',
  unpaid: 'Total approved but unpaid applications of investors which finished KYC',
  kyc_pending:
    'Total pending applications of investors who finished KYC, but it is in verification status',
  incomplete: "Total applications of investors who started but didn't finish KYC",
};

const PendingApplicationSummary = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const AOTParam = params.get(AOTQueryKey);
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const AOTSelectedOption =
    findDefaultValue(chartMonthDateRangeOptions, AOTParam) || chartMonthDateRangeOptions[1];

  const { data: summaryData, isLoading: isSummaryLoading } =
    useIssuerGetPendingApplicationSummarySectionQuery({
      date_to: dayjs().format('YYYY-MM-DDTHH:mm'),
      date_from: AOTSelectedOption.value
        ? dayjs().subtract(+AOTSelectedOption.value, 'days').format('YYYY-MM-DDTHH:mm')
        : undefined,
    });

  const { data: applicationsOverTimeData, isLoading: isApplicationsOverTimeLoading } =
    useIssuerGetPendingApplicationSummaryChartQuery({
      date_to: dayjs().format('YYYY-MM-DDTHH:mm'),
      date_from: AOTSelectedOption.value
        ? dayjs().subtract(+AOTSelectedOption.value, 'days').format('YYYY-MM-DDTHH:mm')
        : undefined,
    });

  const summary = useMemo(() => {
    const result = (summaryData?.data || Array(4).fill({ value: 0 })).map(
      ({ quantity, value, status }) => ({
        title: (
          <div className="flex gap-2 text-xs">
            <span>{mapSummaryKeyToTitle[status]}</span>
            <Tooltip
              button={<img src={InfoCircleIcon} alt="info" width={16} height={16} />}
              classNameTooltip="!top-[24px]"
            >
              {`${mapSummaryKeyToTooltipInfo[status]}`.replace(
                'applications',
                getRegisterTypeStrings('application', { plural: true }),
              )}
            </Tooltip>
          </div>
        ),
        number: quantity,
        footer: <span className="text-xs">{goodFormatUSDCurrencyLowPrecision(value)}</span>,
      }),
    );

    return result;
  }, [getRegisterTypeStrings, summaryData?.data]);

  const applicationsOverTimeChartData = useMemo(
    () => [
      {
        name: mapSummaryKeyToTitle.pending,
        data: adaptApplicationsOverTimeData(applicationsOverTimeData?.data, AOTParam).map(
          ({ pending, date }) => ({
            x: date,
            y: pending,
          }),
        ),
      },
      {
        name: mapSummaryKeyToTitle.unpaid,
        data: adaptApplicationsOverTimeData(applicationsOverTimeData?.data, AOTParam).map(
          ({ awaiting_payment, date }) => ({
            x: date,
            y: awaiting_payment,
          }),
        ),
      },
      {
        name: mapSummaryKeyToTitle.kyc_pending,
        data: adaptApplicationsOverTimeData(applicationsOverTimeData?.data, AOTParam).map(
          ({ kyc_pending, date }) => ({
            x: date,
            y: kyc_pending,
          }),
        ),
      },
      {
        name: mapSummaryKeyToTitle.incomplete,
        data: adaptApplicationsOverTimeData(applicationsOverTimeData?.data, AOTParam).map(
          ({ incomplete, date }) => ({
            x: date,
            y: incomplete,
          }),
        ),
      },
    ],
    [AOTParam, applicationsOverTimeData?.data],
  );

  const handleChangeApplicationOverTimeRange = useCallback(
    (value: string) => {
      params.set(AOTQueryKey, value);
      navigate(
        { search: params.toString() },
        {
          replace: false,
        },
      );
    },
    [navigate, params],
  );

  const { summarySeries, summaryLabels } = useMemo(
    () =>
      (summaryData?.data || []).reduce(
        (acc, curr) => ({
          summarySeries: [...acc.summarySeries, curr.quantity],
          summaryLabels: [...acc.summaryLabels, mapSummaryKeyToTitle[curr.status]],
        }),
        {
          summarySeries: [] as number[],
          summaryLabels: [] as string[],
        },
      ),
    [summaryData?.data],
  );

  const pendingApplicationsSummaryChartOptions = useMemo<ApexOptions>(
    () => ({
      legend: {
        markers: { width: 6, height: 6, offsetY: 4 },
        formatter: (label, { seriesIndex }) => {
          return renderToString(
            <div className="w-[calc(100%_-_20px)] flex flex-row justify-between">
              <span className="text-ellipsis overflow-hidden">{label}</span>
              <span className="font-semibold text-end">
                {summaryData?.data?.[seriesIndex]?.percentage}%
              </span>
            </div>,
          );
        },
      },
      noData: {
        text: 'No data',
        style: {
          fontSize: '18px',
        },
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              name: { formatter: () => 'Total pending' },
              total: {
                formatter: () => goodFormatUSDCurrencyLowPrecision(summaryData?.total_sum || 0),
              },
            },
          },
        },
      },
      tooltip: {
        shared: true,
        cssClass: 'bg-white',
        custom: ({ seriesIndex, series, w }) => {
          // because apex charts skip series with 0 when passes seriesIndex
          const filteredSummaryData = (summaryData?.data || []).filter(
            ({ percentage }) => !!percentage,
          );
          const status = filteredSummaryData[seriesIndex].status;

          return renderToString(
            <div className="w-[260px] p-4 bg-white space-y-2 text-gray-900">
              <div className="flex gap-2 items-center">
                <div
                  className="rounded-full w-1.5 h-1.5"
                  style={{ backgroundColor: w.config.colors[seriesIndex] }}
                />
                <div>{mapSummaryKeyToTitle[status]}</div>
              </div>
              <div className="flex">
                <div className="w-[50%] text-[rgba(23,_23,_23,_0.56)]">Percentage</div>
                <div>{goodFormatNumber(filteredSummaryData[seriesIndex]?.percentage || 0)}%</div>
              </div>
              <div className="flex">
                <div className="w-[50%] text-[rgba(23,_23,_23,_0.56)]">
                  {getRegisterTypeStrings('application', { plural: true, capitalize: true })}
                </div>
                <div>{series[seriesIndex]}</div>
              </div>
              <div className="flex">
                <div className="w-[50%] text-[rgba(23,_23,_23,_0.56)]">Total</div>
                <div>
                  {goodFormatUSDCurrencyLowPrecision(filteredSummaryData[seriesIndex]?.value || 0)}
                </div>
              </div>
            </div>,
          );
        },
      },
    }),
    [getRegisterTypeStrings, summaryData?.data, summaryData?.total_sum],
  );

  const isApplicationOverTimeData = applicationsOverTimeChartData.some(
    ({ data }) => data.length && data.some(({ y }) => y),
  );
  const isSummaryData = summarySeries.some(Boolean);

  return (
    <InfoCard
      title={`Pending ${getRegisterTypeStrings('application', {
        capitalize: true,
        plural: true,
      })} Summary`}
    >
      <div className="w-full h-full flex flex-col gap-6">
        <AnimatedNumbersSet
          loading={isSummaryLoading}
          classes={{
            container: '!border-none !p-0 !grid-cols-1 md:!grid-cols-2 lg:!grid-cols-4',
            item: '!border-none !gap-0',
            itemNumber: '!text-lg sm:!text-lg',
            itemTitle: 'mt-0',
          }}
          numbers={summary}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 divider lg:divide-x">
          <div className="flex flex-col gap-3 lg:pr-6">
            <div className="flex flex-wrap justify-between items-center gap-2">
              <div className="text-sm font-medium">
                Pending{' '}
                {getRegisterTypeStrings('application', {
                  plural: true,
                })}{' '}
                over time
              </div>
              <SelectButtons
                options={chartMonthDateRangeOptions}
                selected={AOTSelectedOption}
                onChange={handleChangeApplicationOverTimeRange}
                className="w-[175px]"
                disabled={isApplicationsOverTimeLoading}
              />
            </div>
            {isApplicationsOverTimeLoading && (
              <div className="w-full h-[245px] flex items-center justify-center">
                <Spinner size="lg" />
              </div>
            )}
            {!isApplicationsOverTimeLoading && !isApplicationOverTimeData && (
              <div className="h-[300px] flex justify-center items-center font-medium text-lg">
                No data for this period
              </div>
            )}
            {!isApplicationsOverTimeLoading && isApplicationOverTimeData && (
              <BalanceChartCanvas
                height="300"
                fill={false}
                type="line"
                data={applicationsOverTimeChartData}
                colors={applicationsOverTimeChartColors}
                countsLabel={6}
                markerStrokeColor="#FFFFFF"
              />
            )}
          </div>
          <div className="flex flex-col gap-3 lg:pl-6">
            <div className="h-9 flex items-center">
              <p className="font-medium text-sm">
                Pending{' '}
                {getRegisterTypeStrings('application', {
                  plural: true,
                })}{' '}
                received
              </p>
            </div>
            <div>
              {isSummaryLoading && (
                <div className="w-full h-[222px] flex items-center justify-center">
                  <Spinner size="lg" />
                </div>
              )}
              {!isSummaryLoading && !isSummaryData && (
                <div className="h-[300px] flex justify-center items-center font-medium text-lg">
                  No data for this period
                </div>
              )}
              {!isSummaryLoading && isSummaryData && (
                <BalancePieChart
                  series={summarySeries}
                  labels={summaryLabels}
                  options={pendingApplicationsSummaryChartOptions}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </InfoCard>
  );
};

export default PendingApplicationSummary;
